// GAVION ai-email (spec §5): the 5 default rules of the AI email assistant. R20–R23 queue proposed AI drafts; R24
// (ruling P2) keeps the J+4 callback of « Brochure envoyée » once R11 (template auto-send + callback) is turned off.
// Seeded by 51_gavion_ai_email.sql (the `-- BEGIN ai-rules` literal; scripts/gavion/opsParity.test.ts keeps it equal
// to this list). Kept apart from DEFAULT_RULES so the batch-2 seed (22_gavion_dialer_rules_seed.sql, applied) never
// changes.
import type { DefaultRule } from "./defaultRules.ts";

/**
 * Spec §7 (plan D5, owner ruling): default rules that send a TEMPLATE email to one lead and that the AI draft replaces:
 * R4 (voicemail follow-up) and R11 (automatic brochure send; its J+4 callback lives on in R24). Switched off once by 51
 * (and in the demo); kept in DEFAULT_RULES so an admin can switch them back on. R8 (demo confirmation, transactional)
 * stays on.
 */
export const AI_REPLACED_RULE_KEYS = ["R4", "R11"] as const;

/**
 * GAVION missed-call: « Message laissé » no longer queues an AI draft — the automatic « Appel manqué » email (R25,
 * missedCallRules.ts) covers it, so the lead never gets two emails. 55_gavion_missed_call_email.sql updates R20's
 * condition (default rule, default condition only); AI_AFTER_CALL_OUTCOMES_51 is what 51 seeded (applied, unchanged).
 */
export const AI_AFTER_CALL_OUTCOMES = [
  "talked_callback",
  "brochure_sent",
] as const;
export const AI_AFTER_CALL_OUTCOMES_51 = [
  "voicemail",
  ...AI_AFTER_CALL_OUTCOMES,
] as const;
/** 10:00 Toronto, Monday to Friday (schedule rules are read in Toronto wall time); holidays skipped in SQL. */
export const AI_STALE_CRON = "0 10 * * 1-5";

export const AI_DEFAULT_RULES = [
  {
    key: "R20",
    position: 200,
    name: "Après un appel → brouillon IA de suivi",
    trigger: "call_outcome",
    conditions: {
      field: "call.outcome",
      op: "in",
      value: [...AI_AFTER_CALL_OUTCOMES],
    },
    actions: [{ type: "ai_draft", trigger: "after_call" }],
  },
  {
    key: "R21",
    position: 210,
    name: "Réponse du client → brouillon IA de réponse",
    trigger: "email_event",
    conditions: { field: "type", op: "eq", value: "reply_received" },
    actions: [{ type: "ai_draft", trigger: "client_reply" }],
  },
  {
    key: "R22",
    position: 220,
    name: "Notes de démo (Granola) → brouillon IA de suivi",
    trigger: "meeting_note_matched",
    conditions: {
      all: [
        { field: "deal.id", op: "exists" },
        { field: "deal.demo_at", op: "exists" },
      ],
    },
    actions: [{ type: "ai_draft", trigger: "after_demo" }],
  },
  {
    key: "R23",
    position: 230,
    name: "Lead sans nouvelles → brouillon IA de relance",
    trigger: "schedule",
    conditions: { cron: AI_STALE_CRON },
    actions: [{ type: "ai_draft", trigger: "stale_lead" }],
  },
  {
    key: "R24",
    position: 240,
    name: "Brochure envoyée → rappel J+4",
    trigger: "call_outcome",
    conditions: { field: "call.outcome", op: "eq", value: "brochure_sent" },
    actions: [{ type: "set_callback", business_days: 4, at: "10:00" }],
  },
] satisfies readonly DefaultRule[];
